<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaUserPickerList from '@/components/design/domain/people/GaiaUserPickerList.vue';
import type { GaiaPickerListInstance } from '@/components/design/domain/people/types';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';

import { IconUserRemove } from '@/components/design/icons';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { describeVaultCounts, type VaultUidGroups } from '@/utils/vaultSelection';

/**
 * Change the owner of vault items: files, contracts and folders, one or many.
 * Everything but a single file goes through the bulk endpoint, which is org
 * admins only; a contract's files follow the contract.
 */
const props = withDefaults(
  defineProps<{
    show: boolean;
    /** One item (row action) or the selection (bulk). */
    mode: 'single' | 'bulk';
    groups: VaultUidGroups;
    /** Show "Unassign owner" (only org admins may unassign). */
    allowUnassign?: boolean;
    /** Current owner display names (e.g. ["Doruk Arslan"] or ["X", "Y", "Z"]). */
    currentOwnerLabels?: string[];
  }>(),
  { allowUnassign: false, currentOwnerLabels: () => [] }
);

const emit = defineEmits<{
  'update:show': [value: boolean];
  reassigned: [];
}>();

const vaultStore = useVaultStore();
const message = useGaiaToast();

const selectedUserUid = ref<string | null>(null);
const submitting = ref(false);
const errorMessage = ref('');
const pickerRef = ref<GaiaPickerListInstance | null>(null);

const totalCount = computed(
  () =>
    props.groups.fileUids.length +
    props.groups.contractUids.length +
    props.groups.directoryUids.length
);
const hasSelection = computed(() => totalCount.value > 0);
const hasDirectories = computed(() => props.groups.directoryUids.length > 0);
/** A lone file can go through its own update, open to whoever holds ADMIN on it. */
const isSingleFile = computed(() => props.groups.fileUids.length === 1 && totalCount.value === 1);
/** Backend default is recursive: folder + all subfolders, files and contracts. */
const reassignRecursively = ref(true);
const title = computed(() => {
  if (props.mode === 'single') {
    if (props.groups.directoryUids.length > 0) return 'Change owner (folder)';
    if (props.groups.contractUids.length > 0) return 'Change contract owner';
    return 'Change file owner';
  }
  return `Reassign owner (${describeVaultCounts({
    files: props.groups.fileUids.length,
    contracts: props.groups.contractUids.length,
    folders: props.groups.directoryUids.length,
  })})`;
});

/** e.g. "Unassign owner: Doruk Arslan" (single) or "Unassign owners" (bulk) */
const unassignLabel = computed(() => {
  if (props.mode === 'bulk') return 'Unassign owners';
  const labels = props.currentOwnerLabels.filter(Boolean);
  const name = labels[0];
  return name ? `Unassign owner: ${name}` : 'Unassign owner';
});

watch(
  () => props.show,
  visible => {
    if (visible) {
      selectedUserUid.value = null;
      errorMessage.value = '';
      reassignRecursively.value = true;
      // The list keeps its own term and page, so reopening asks it to start over.
      pickerRef.value?.reset();
    }
  }
);

async function applyUnassign(): Promise<void> {
  if (!hasSelection.value || !props.allowUnassign || submitting.value) return;
  submitting.value = true;
  errorMessage.value = '';
  try {
    const result = await vaultStore.bulkReassignVaultOwner(
      props.groups,
      null,
      reassignRecursively.value
    );
    // Backend message includes the real counts ("... N file(s), N contract(s) and N directory(ies)").
    message.success(
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      result?.message || (totalCount.value === 1 ? 'Owner unassigned' : 'Owners unassigned')
    );
    emit('reassigned');
    close();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to unassign owner';
  } finally {
    submitting.value = false;
  }
}

function close(): void {
  emit('update:show', false);
  selectedUserUid.value = null;
  errorMessage.value = '';
}

async function handleSubmit(): Promise<void> {
  if (!hasSelection.value) return;
  const uid = selectedUserUid.value;
  if (!uid) {
    errorMessage.value = 'Please select an owner';
    return;
  }

  submitting.value = true;
  errorMessage.value = '';

  try {
    if (isSingleFile.value) {
      await vaultStore.updateFile(props.groups.fileUids[0], { owner_user_uid: uid });
      message.success('Owner updated');
    } else {
      const result = await vaultStore.bulkReassignVaultOwner(
        props.groups,
        uid,
        reassignRecursively.value
      );
      // Backend message includes the real counts.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      message.success(result?.message || 'Owners updated');
    }
    emit('reassigned');
    close();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to update owner';
  } finally {
    submitting.value = false;
  }
}

/**
 * Put on the modal card as inline custom properties rather than bound through
 * `v-bind()`. This component's root is a modal, i.e. a Teleport — Vue writes
 * `v-bind()` vars onto the component's own root element, and a teleported
 * subtree has none in place, so every colour bound that way silently resolved
 * to nothing. On the card they cascade to the header, body and footer alike.
 */
const styleVars = {
  '--reassign-accent': gaiaAccent.base,
  '--reassign-accent-surface': gaiaAccent.wash,
  '--reassign-card-border': gaiaNeutral.borderCard,
  '--reassign-row-size': gaiaFontSize.row,
  '--reassign-row-weight': String(gaiaFontWeight.medium),
  '--reassign-soft-surface': gaiaNeutral.surfaceSoft,
};
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="560px"
    :mask-closable="false"
    :style="styleVars"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">{{ title }}</gaia-text>
    </template>

    <n-flex vertical :size="14">
      <!-- Only meaningful when folders are in the selection: it decides whether
           the change reaches their contents. Governs unassign as well. -->
      <template v-if="hasDirectories">
        <n-flex vertical :size="4">
          <gaia-checkbox v-model:checked="reassignRecursively">
            Include folder contents
          </gaia-checkbox>
          <gaia-text size="meta" tone="subtle">
            {{
              reassignRecursively
                ? 'Applies to the folders, every subfolder, and every file and contract inside.'
                : 'Applies to the selected folders only; their contents keep their current owners.'
            }}
          </gaia-text>
        </n-flex>
        <gaia-divider variant="subtle" :style="{ margin: 0 }" />
      </template>

      <!-- Handing a file back to the organisation, rather than to a person. -->
      <gaia-tooltip :disabled="props.allowUnassign">
        <template #trigger>
          <div>
            <button
              type="button"
              class="unassign"
              :disabled="!props.allowUnassign || submitting"
              @click="applyUnassign()"
            >
              <gaia-spinner v-if="submitting" size="small" />
              <n-icon v-else :component="IconUserRemove" :size="20" />
              {{ unassignLabel }}
            </button>
          </div>
        </template>
        Only organisation admins can leave an item unassigned.
      </gaia-tooltip>

      <gaia-divider variant="subtle" :style="{ margin: 0 }" />

      <!-- The shared people picker (search, list, paging) — the same list every
           assign-a-user surface uses since the unify-inputs pass. -->
      <gaia-user-picker-list ref="pickerRef" v-model:value="selectedUserUid" :page-size="10" />

      <gaia-banner v-if="errorMessage" type="error">{{ errorMessage }}</gaia-banner>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="submitting" @click="close">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="submitting"
          :disabled="!selectedUserUid"
          @click="handleSubmit"
        >
          {{ props.mode === 'single' ? 'Update owner' : 'Reassign' }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* Colours come from the `styleVars` map on the body, not `v-bind()` — see the
   note there. A one-off card: it is the "give it back to the organisation"
   choice, so it sits on a surface of its own above the list of people. */
.unassign {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--reassign-card-border);
  border-radius: 10px;
  background: var(--reassign-soft-surface);
  font-family: inherit;
  font-size: var(--reassign-row-size);
  font-weight: var(--reassign-row-weight);
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.unassign:hover:not(:disabled) {
  border-color: var(--reassign-accent);
  background: var(--reassign-accent-surface);
}

.unassign:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
